import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState, isSSR } from '../../common/utils';
import { Logger } from '../../common/utils/logger';
import { Status, type SetItemResult } from '../../types';
import { type AtlIndexedDBItem, BSCIndexedDB } from '../indexed-db-service';

import { WebStorageType } from './types';
import { getStorageItem, setStorageItem, setStrictlyNecessaryStorageItem } from './utils';

class AtlBrowserStorage {
	storage: Storage;
	storageType: WebStorageType;
	isAvailable = false;

	constructor(storage: Storage, storageType: WebStorageType) {
		this.storage = storage;
		this.storageType = storageType;
		this.isAvailable = this.isStorageAvailable();
	}

	get isWebStorageEnabled(): boolean {
		// Note: outside of user configuration, webStorageEnabled is also feature gated by
		// 1. platform_moonjelly_bsc_master_killswitch
		// 2. platform_moonjelly_bsc_web_storage_killswitch
		if (getBscGlobalState().webStorageEnabled) {
			return true;
		}
		Logger.debug('WebStorage config is disabled, cannot proceed.');
		return false;
	}

	// Core API
	getItem(key: string): string | null {
		if (!this.isWebStorageEnabled) {
			return this.storage.getItem(key);
		}

		if (!this.isAvailable) {
			// If our storage hasn't been enabled, short-circuit because we can't use the native utility
			Logger.warn(`${this.storageType} is unavailable. Failed to get item ${key}`);
			return null;
		}

		const retrievedItem = getStorageItem(key, this.storageType, this.storage);
		return retrievedItem;
	}

	async setItem(key: string, value: string): Promise<SetItemResult | undefined> {
		// IMPORTANT: do not await above the setStorageItem call - if you do it will cause reading after writing to not return the new value until the microtask queue is drained, instead of just those that need to wait for remote fetch.

		const isSetItemReturnsStatusEnabled = fg('platform_moonjelly_bsc_set_item_returns_status');

		if (!this.isWebStorageEnabled) {
			this.storage.setItem(key, value);
			if (isSetItemReturnsStatusEnabled) {
				return { status: Status.SUCCESS };
			}
			return;
		}

		if (!this.isAvailable) {
			// If our storage hasn't been enabled, short-circuit because we can't use the native utility
			Logger.warn(`${this.storageType} is unavailable. Failed to set item ${key}`);
			if (isSetItemReturnsStatusEnabled) {
				return {
					status: Status.ERROR,
					error: new Error(`${this.storageType} is unavailable. Failed to set item ${key}`),
				};
			}
			return;
		}

		const globalStateProduct = getBscGlobalState().product;

		// IMPORTANT: do not await above this line - if you do it will cause reading after writing to not return the new value until the microtask queue is drained, instead of just those that need to wait for remote fetch.
		const itemSetResult = await setStorageItem(
			key,
			value,
			this.storageType,
			globalStateProduct,
			this.storage,
		);
		Logger.debug(
			`${itemSetResult.status === Status.SUCCESS ? 'Successfully' : 'Failed to'} set item ${key} with value ${value}`,
		);
		if (isSetItemReturnsStatusEnabled) {
			return itemSetResult;
		}
		return;
	}

	setStrictlyNecessaryItem(key: string, value: string): void {
		if (!this.isWebStorageEnabled) {
			this.storage.setItem(key, value);
			return;
		}

		if (!this.isAvailable) {
			// If our storage hasn't been enabled, short-circuit because we can't use the native utility
			Logger.warn(
				`${this.storageType} is unavailable. Failed to set strictly necessary item ${key}`,
			);
			return;
		}

		const itemSetStatus = setStrictlyNecessaryStorageItem(
			key,
			value,
			this.storageType,
			this.storage,
		);
		Logger.debug(
			`${itemSetStatus === Status.SUCCESS ? 'Successfully' : 'Failed to'} set strictly necessary item ${key} with value ${value}`,
		);
	}

	// Native Utilities
	removeItem(key: string): void {
		if (!this.isAvailable) {
			// If our storage hasn't been enabled, short-circuit because we can't use the native utility
			Logger.warn(`${this.storageType} is unavailable. Failed to remove item ${key}`);
			return;
		}

		this.storage.removeItem(key);
	}

	key(index: number): string | null {
		if (!this.isAvailable) {
			return null;
		}

		return this.storage.key(index);
	}

	length(): number {
		if (!this.isAvailable) {
			return 0;
		}

		return this.storage.length;
	}

	clear(): void {
		if (!this.isAvailable) {
			return;
		}

		this.storage.clear();
	}

	// Utilities
	isStorageAvailable(): boolean {
		if (isSSR()) {
			return false;
		}
		try {
			const x = '__storage_test__';
			this.storage.setItem(x, x);
			this.storage.removeItem(x);
			return true;
		} catch (e) {
			return (
				e instanceof DOMException &&
				// everything except Firefox
				(e.code === 22 ||
					// Firefox
					e.code === 1014 ||
					// test name field too, because code might not be present
					// everything except Firefox
					e.name === 'QuotaExceededError' ||
					// Firefox
					e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
				// acknowledge QuotaExceededError only if there's something already stored
				this.storage &&
				this.storage.length !== 0
			);
		}
	}

	checkIfSetByPackage = async ({
		itemKey,
		isSetCallback = () => {},
		isNotSetCallback = () => {},
	}: {
		itemKey: string;
		isSetCallback?: () => void;
		isNotSetCallback?: () => void;
	}): Promise<boolean> => {
		// As IDB is asynchronous in nature, when this in invoked, we need to rely on the callbacks
		// to actually do something with the result.
		try {
			const record: AtlIndexedDBItem = await BSCIndexedDB.getRecord(itemKey);
			if (record) {
				isSetCallback();
				return true;
			} else {
				isNotSetCallback();
				return false;
			}
		} catch (e: any) {
			// If it fails to pull from IDB, we can't validate that it's been set by our package or not, so log the error
			// and assume it hasn't been set by our package. This will only impact our analytics correctness.
			Logger.errorWithOperationalEvent({
				action: 'usedCheckIfSetByPackageError',
				message: `IDB lookup failed to check if item was set by package. ${e.message || ''}`,
				attributes: {
					itemKey,
				},
			});
			isNotSetCallback();
			return false;
		}
	};
}

const AtlBrowserStorageLocal: AtlBrowserStorage = new AtlBrowserStorage(
	globalThis.localStorage,
	WebStorageType.LocalStorage,
);

const AtlBrowserStorageSession: AtlBrowserStorage = new AtlBrowserStorage(
	globalThis.sessionStorage,
	WebStorageType.SessionStorage,
);

export const findStorageTarget = (type: WebStorageType): AtlBrowserStorage => {
	return {
		[WebStorageType.LocalStorage]: AtlBrowserStorageLocal,
		[WebStorageType.SessionStorage]: AtlBrowserStorageSession,
	}[type];
};

export { AtlBrowserStorage, AtlBrowserStorageSession, AtlBrowserStorageLocal, WebStorageType };
